import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Dropdown, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import FalconCloseButton from "components/common/falconCloseButton";
import Flex from "components/common/flex";
import { useTranslation } from "react-i18next";
import SearchResults from "./searchResults";
import axios from "axios";
import { USER_ROLES } from "config/roles.config";

const MIN_QUERY_LENGTH = 3; // ← changed from 1 to 3 (backend requires min 3 chars)
const DEBOUNCE_MS = 200;    // ← debounce window

const STATUS_VARIANT = {
    Agreed: "primary",
    Open: "success",
    ProposalSent: "info",
    NotAgreed: "warning",
    Close: "danger",
    Draft: "secondary",
    Active: "success",
    Completed: "primary",
};

export const getStatusVariant = (status) => {
    const key = status?.replace(/\s+/g, "") ?? "";
    return STATUS_VARIANT[key] ?? "secondary";
};

export const getCategoryConfig = (t) => ({
    orders: {
        avatarBg: "bg-success-subtle",
        avatarText: "text-success",
        avatarLabel: "O",
        getTitle: (item) => item.OrderNumber,
        getSubtitle: (item) => item.ClientName ?? item.TemplateName ?? "—",
        getRight: (item) =>
            item.StartDate
                ? new Date(item.StartDate).toLocaleDateString("en-GB").replace(/\//g, "-")
                : "—",
        getBadge: (item) => {
            const statusKey = item.Status?.toLowerCase?.() ?? "";
            return {
                label: t(statusKey) || item.Status,
                variant: getStatusVariant(item.Status),
            };
        },
    },
    proposals: {
        avatarBg: "bg-primary-subtle",
        avatarText: "text-primary",
        avatarLabel: "P",
        getTitle: (item) => item.ProposalNumber,
        getSubtitle: (item) => item.ClientName ?? "—",
        getRight: (item) =>
            item.StartDate
            ? new Date(item.StartDate).toLocaleDateString("en-GB").replace(/\//g, "-")
            : "—",
        getBadge: (item) => {
            const label =
                item.Status === "0"
                    ? t("open")
                    : item.Status === "1"
                    ? t("agreed")
                    : item.Status === "2"
                    ? t("notAgreed")
                    : t("proposalSent");

            const variant =
                item.Status === "0"
                    ? getStatusVariant("Open")
                    : item.Status === "1"
                    ? getStatusVariant("Agreed")
                    : item.Status === "2"
                    ? getStatusVariant("NotAgreed")
                    : getStatusVariant("ProposalSent");

            return { label, variant };
        },
    },
    relations: {
        avatarBg: "bg-info-subtle",
        avatarText: "text-info",
        avatarLabel: "R",
        getTitle: (item) => item.Name,
        getSubtitle: (item) => item.Contactperson ?? "—",
    },
    users: {
        avatarBg: "bg-warning-subtle",
        avatarText: "text-warning",
        getAvatarLabel: (item) =>
            (item.Name ?? "?")
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase(),
        getTitle: (item) => item.Name,
        getSubtitle: (item) => item.Emailaddress ?? "—",
    },
});

const fetchSearchResults = async (query, customerId, locationId) => {
    const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/searchRoute`,
        {
            headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret,
            },
            params: { q: query, customerId, locationId },
        }
    );
    return response.data;
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const GlobalSearchBox = ({ tabletIconOnly = false }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState("");
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedSection, setExpandedSection] = useState(null);
    const [tabletSearchOpen, setTabletSearchOpen] = useState(false);
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const timerRef = useRef(null);

    const customerId = localStorage.getItem("customerId")
        ?? sessionStorage.getItem("customerId")
        ?? "";
    const locationId = localStorage.getItem("LocationId");

    // ── Outside click handler ─────────────────────────────────────────────────
    useEffect(() => {
        if (tabletIconOnly) return;
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setDropdownOpen(false);
                setSearchInputValue("");
                setExpandedSection(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [tabletIconOnly]);

    // ── Debounced API call ────────────────────────────────────────────────────
    useEffect(() => {
        const q = searchInputValue.trim();

        if (q.length < MIN_QUERY_LENGTH) {
            setResults(null);
            setDropdownOpen(false);
            setLoading(false);
            setExpandedSection(null);
            clearTimeout(timerRef.current);
            return;
        }

        setLoading(true);
        setDropdownOpen(true);
        clearTimeout(timerRef.current);

        timerRef.current = setTimeout(async () => {
            try {
                const data = await fetchSearchResults(q, customerId, locationId);
                setResults(data);
            } catch (err) {
                console.error("[GlobalSearch] API error →", err);
                setResults({ orders: [], proposals: [], relations: [], users: [] });
            } finally {
                setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timerRef.current);
    }, [searchInputValue, customerId]);

    // ── Tablet search open/close ──────────────────────────────────────────────
    useEffect(() => {
        if (tabletSearchOpen) {
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            setSearchInputValue("");
            setDropdownOpen(false);
            setExpandedSection(null);
        }
    }, [tabletSearchOpen]);

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === "Escape") {
                setDropdownOpen(false);
                setTabletSearchOpen(false);
                setExpandedSection(null);
                inputRef.current?.blur();
            }
        },
        []
    );

    const handleClear = () => {
        setSearchInputValue("");
        setDropdownOpen(false);
        setResults(null);
        setExpandedSection(null);
    };

    const handleClose = useCallback(() => {
        setSearchInputValue("");
        setDropdownOpen(false);
        setResults(null);
        setExpandedSection(null);
        setTabletSearchOpen(false);
    }, []);

    // ── Employee / Employee-view guard ────────────────────────────────────────
    const userRole = localStorage.getItem("UserRole");
    const isEmployeeView = sessionStorage.getItem("isEmployeeView") === "true";
    if (userRole === USER_ROLES.EMPLOYEE || userRole === USER_ROLES.CLIENT || isEmployeeView) return null;

    // ── Tablet view ───────────────────────────────────────────────────────────
    if (tabletIconOnly) {
        return (
            <>
                <button
                    className="p-0 nav-link"
                    onClick={() => setTabletSearchOpen(true)}
                    aria-label="Open search"
                >
                    <div className="search-icon-btn d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle">
                        <FontAwesomeIcon icon={faSearch} className="text-primary fs-9" />
                    </div>
                </button>

                {tabletSearchOpen && (
                    <>
                        <div
                            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25 search-backdrop"
                            onClick={() => setTabletSearchOpen(false)}
                        />
                        <div className="position-fixed start-50 translate-middle-x search-panel">
                            <div className="bg-white shadow rounded-3 border overflow-hidden">
                                <Flex alignItems="center" className="px-4 py-2 border-bottom gap-2">
                                    <FontAwesomeIcon icon={faSearch} className="text-400" />
                                    <Form.Control
                                        ref={inputRef}
                                        type="text"
                                        placeholder={t("searchGlobal")}
                                        aria-label="Global search"
                                        className="border-0 shadow-none p-0 fs-9 flex-1"
                                        value={searchInputValue}
                                        onChange={({ target }) => setSearchInputValue(target.value)}
                                        onKeyDown={handleKeyDown}
                                    />
                                    <FalconCloseButton
                                        size="sm"
                                        noOutline
                                        className="fs-11"
                                        onClick={() => setTabletSearchOpen(false)}
                                    />
                                </Flex>
                                <div className="search-results-scroll overflow-y-auto overflow-hidden scrollbar flex-grow-1">
                                    <SearchResults
                                        results={results}
                                        loading={loading}
                                        expandedSection={expandedSection}
                                        setExpandedSection={setExpandedSection}
                                        searchInputValue={searchInputValue}
                                        t={t}
                                        onClose={handleClose}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    }

    // ── Desktop view ──────────────────────────────────────────────────────────
    return (
        <Dropdown show={dropdownOpen} className="search-box w-100" ref={containerRef}>
            <Dropdown.Toggle as="div" className="dropdown-caret-none">
                <Form className="position-relative" onSubmit={(e) => e.preventDefault()}>
                    <Form.Control
                        ref={inputRef}
                        type="search"
                        placeholder={t("searchGlobal")}
                        aria-label="Global search"
                        className="rounded-2 search-input w-100"
                        value={searchInputValue}
                        onChange={({ target }) => setSearchInputValue(target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <FontAwesomeIcon
                        icon={faSearch}
                        className="position-absolute text-400 search-box-icon"
                    />
                    {(dropdownOpen || searchInputValue) && (
                        <div className="search-box-close-btn-container">
                            <FalconCloseButton size="sm" noOutline className="fs-11" onClick={handleClear} />
                        </div>
                    )}
                </Form>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-card py-2 w-100">
                <div className="search-results-scroll overflow-y-auto overflow-hidden scrollbar flex-grow-1">
                    <SearchResults
                        results={results}
                        loading={loading}
                        expandedSection={expandedSection}
                        setExpandedSection={setExpandedSection}
                        searchInputValue={searchInputValue}
                        t={t}
                        onClose={handleClose}
                    />
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

GlobalSearchBox.propTypes = {
    tabletIconOnly: PropTypes.bool,
};

export default GlobalSearchBox;